import { AccountHistoricValue } from "@core/models/account-historic-values.model";
import { AccountRiskProfile } from "@core/models/account-risk-profile.model";
import { CompositionRiskProfile } from "@core/models/composition-risk-profile.model";
import { CompositionVersion } from "@core/models/composition-version.model";
import { Configuration } from "@core/models/configuration.model";
import { DisbursementWithTax } from "@core/models/disbursement-with-tax.model";
import { AccountFund } from "@core/models/fund.model";
import { FundRatioHistory } from "@core/models/fund-ratio-history.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { GeneralAccountCorrection } from "@core/models/general-account-correction.model";
import { GroupedGfhTransaction } from "@core/models/grouped-gfh-transaction.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { TAllClients } from "@core/models/user.model";
import { CompanyUser } from "@core/models/user-company.model";
import { TPagination } from "@custom-types/sorting";
import * as ClientsAndAccountsActions from "@features/clients-and-accounts/store/clients-and-accounts.actions";
import { ExtendedGfhAccount } from "@models/extended-gfh-account.model";
import { InternalTransfer } from "@models/internal-transfer.model";
import { Quotation } from "@models/quotation.model";
import { PrivateUser } from "@models/user-private.model";
import { WithdrawalAssignment } from "@models/withdrawal-assignment.model";
import { createReducer, on } from "@ngrx/store";

export const CLIENTS_AND_ACCOUNTS_FEATURE_KEY = "clientsAndAccounts";

export interface ClientsAndAccountsState {
  allClients: Array<TAllClients>;
  allClientsPaginated: Array<TAllClients>;
  privateClients: Array<PrivateUser>;
  companyClients: Array<CompanyUser>;
  accountList: Array<ExtendedGfhAccount>;
  correctionList: Array<GeneralAccountCorrection>;
  transactions: Array<GroupedGfhTransaction>;
  accountHistoricValueList: Array<AccountHistoricValue>;
  fundList: Array<FundWithRelated>;
  withdrawalAssignmentList: Array<WithdrawalAssignment>;
  internalTransferList: Array<InternalTransfer>;
  disbursements: Array<DisbursementWithTax>;
  quotationList: Array<Quotation>;
  activeAccount: ExtendedGfhAccount;
  accountConfiguration: Configuration;
  bsn: string;
  loading: boolean;
  accountsLoading: boolean;
  correctionLoading: boolean;
  transactionsLoading: boolean;
  disbursementsLoading: boolean;
  fundListLoading: boolean;
  valid: boolean;
  pauseReasonStatus: boolean;
  riskProfiles: Array<RiskProfile | CompositionRiskProfile>;
  compositionFundList: Array<CompositionVersion>;
  impersonationAccountRequest: string;
  accountRiskProfiles: Array<AccountRiskProfile>;
  fundRatioHistory: Array<FundRatioHistory>;
  emailExists: boolean;
  pagination: Partial<TPagination> | undefined;
  clientListLength: number;
  accountFundList: Array<AccountFund>;
  leiExists: boolean;
}

const initialState: ClientsAndAccountsState = {
  allClients: [],
  allClientsPaginated: [],
  privateClients: [],
  companyClients: [],
  accountList: [],
  correctionList: [],
  transactions: [],
  accountHistoricValueList: [],
  fundList: [],
  withdrawalAssignmentList: [],
  internalTransferList: [],
  disbursements: [],
  quotationList: [],
  activeAccount: new ExtendedGfhAccount(),
  accountConfiguration: new Configuration(),
  bsn: "",
  loading: false,
  accountsLoading: false,
  correctionLoading: false,
  transactionsLoading: false,
  fundListLoading: false,
  disbursementsLoading: false,
  valid: true,
  pauseReasonStatus: false,
  riskProfiles: [],
  compositionFundList: [],
  impersonationAccountRequest: "",
  accountRiskProfiles: [],
  fundRatioHistory: [],
  emailExists: false,
  pagination: undefined,
  clientListLength: 0,
  accountFundList: [],
  leiExists: false,
};

export const CLIENTS_AND_ACCOUNTS_REDUCER = createReducer(
  initialState,
  on(
    ClientsAndAccountsActions.SET_ALL_CLIENTS,
    (
      state: ClientsAndAccountsState,
      {
        clientsList: allClients,
      }: {
        clientsList: Array<TAllClients>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        allClients,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_ALL_CLIENTS_PAGINATED,
    (
      state: ClientsAndAccountsState,
      {
        clientsList: allClientsPaginated,
      }: {
        clientsList: Array<TAllClients>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        allClientsPaginated,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_PRIVATE_CLIENTS,
    (
      state: ClientsAndAccountsState,
      {
        clientsList: privateClients,
      }: {
        clientsList: Array<PrivateUser>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        privateClients,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_COMPANY_CLIENTS,
    (
      state: ClientsAndAccountsState,
      {
        clientsList: companyClients,
      }: {
        clientsList: Array<CompanyUser>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        companyClients,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS,
    (
      state: ClientsAndAccountsState,
      {
        loading,
      }: {
        loading: boolean;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        loading,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_CLIENT_ACCOUNTS,
    (
      state: ClientsAndAccountsState,
      {
        accountList,
      }: {
        accountList: Array<ExtendedGfhAccount>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        accountList,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS,
    (
      state: ClientsAndAccountsState,
      {
        loading: accountsLoading,
      }: {
        loading: boolean;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        accountsLoading,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_CLIENT_BSN_VALIDITY,
    (
      state: ClientsAndAccountsState,
      {
        valid,
      }: {
        valid: boolean;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        valid,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_ACCOUNT_DETAILS,
    (
      state: ClientsAndAccountsState,
      {
        account: activeAccount,
      }: {
        account: ExtendedGfhAccount;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        activeAccount,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS,
    (
      state: ClientsAndAccountsState,
      {
        transactions,
      }: {
        transactions: Array<GroupedGfhTransaction>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        transactions,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS_LOADING_STATUS,
    (
      state: ClientsAndAccountsState,
      {
        loading: transactionsLoading,
      }: {
        loading: boolean;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        transactionsLoading,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_GENERAL_ACCOUNT_CORRECTION_LIST,
    (
      state: ClientsAndAccountsState,
      {
        correctionList,
      }: {
        correctionList: Array<GeneralAccountCorrection>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        correctionList,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_CORRECTION_LOADING_STATUS,
    (
      state: ClientsAndAccountsState,
      {
        loading: correctionLoading,
      }: {
        loading: boolean;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        correctionLoading,
      };
    }
  ),

  on(
    ClientsAndAccountsActions.SET_ACCOUNT_HISTORIC_VALUE_LIST,
    (
      state: ClientsAndAccountsState,
      {
        accountHistoricValueList,
      }: {
        accountHistoricValueList: Array<AccountHistoricValue>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        accountHistoricValueList,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS,
    (
      state: ClientsAndAccountsState,
      action: {
        loading: boolean;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        fundListLoading: action.loading,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_FUND_LIST,
    (
      state: ClientsAndAccountsState,
      action: {
        fundList: Array<FundWithRelated>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        fundList: action.fundList,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_ACCOUNT_CONFIGURATION,
    (
      state: ClientsAndAccountsState,
      action: {
        configuration: Configuration;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        accountConfiguration: action.configuration,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_WITHDRAWAL_ASSIGNMENT,
    (
      state: ClientsAndAccountsState,
      action: {
        withdrawalList: Array<WithdrawalAssignment>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        withdrawalAssignmentList: action.withdrawalList,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_ACCOUNT_DISBURSEMENTS,
    (
      state: ClientsAndAccountsState,
      action: {
        disbursements: Array<DisbursementWithTax>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        disbursements: action.disbursements,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_ACCOUNT_DISBURSEMENTS_LOADING_STATUS,
    (
      state: ClientsAndAccountsState,
      action: {
        loading: boolean;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        disbursementsLoading: action.loading,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_PAUSE_REASON_STATUS,
    (
      state: ClientsAndAccountsState,
      action: {
        pauseReasonStatus: boolean;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        pauseReasonStatus: action.pauseReasonStatus,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_INTERNAL_TRANSFER_LIST,
    (
      state: ClientsAndAccountsState,
      action: {
        internalTransferList: Array<InternalTransfer>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        internalTransferList: action.internalTransferList,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_QUOTATIONS,
    (
      state: ClientsAndAccountsState,
      action: {
        quotationList: Array<Quotation>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        quotationList: action.quotationList,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_RISK_PROFILES,
    (
      state: ClientsAndAccountsState,
      action: {
        riskProfiles: Array<RiskProfile | CompositionRiskProfile>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        riskProfiles: action.riskProfiles,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_COMPOSITION_PRODUCT_FUNDS,
    (
      state: ClientsAndAccountsState,
      action: {
        compositionFundList: Array<CompositionVersion>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        compositionFundList: action.compositionFundList,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_IMPERSONATION_ACCOUNT_REQUEST,
    (
      state: ClientsAndAccountsState,
      action: {
        impersonationAccountRequest: string;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        impersonationAccountRequest: action.impersonationAccountRequest,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_ACCOUNT_RISK_PROFILES,
    (
      state: ClientsAndAccountsState,
      action: {
        accountRiskProfiles: Array<AccountRiskProfile>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        accountRiskProfiles: action.accountRiskProfiles,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_FUND_RATIO_HISTORY,
    (
      state: ClientsAndAccountsState,
      action: {
        fundRatioHistory: Array<FundRatioHistory>;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        fundRatioHistory: action.fundRatioHistory,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION,
    (
      state: ClientsAndAccountsState,
      action: {
        pagination: Partial<TPagination> | undefined;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        pagination: action.pagination,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH,
    (
      state: ClientsAndAccountsState,
      action: {
        length: number;
      }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        clientListLength: action.length,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_EMAIL_VALIDITY,
    (
      state: ClientsAndAccountsState,
      { emailExists }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        emailExists,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_ACCOUNT_FUNDS,
    (
      state: ClientsAndAccountsState,
      action: { accountFundList: Array<AccountFund> }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        accountFundList: action.accountFundList,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.SET_LEI_NUMBER_VALIDITY,
    (
      state: ClientsAndAccountsState,
      { leiExists }
    ): ClientsAndAccountsState => {
      return {
        ...state,
        leiExists,
      };
    }
  ),
  on(
    ClientsAndAccountsActions.CLEAR_CUSTOMERS_AND_ACCOUNTS_STATE,
    (): ClientsAndAccountsState => {
      return {
        ...initialState,
      };
    }
  )
);
